import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, GraduationCap, Loader2, X, ClipboardCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTrainingStore } from '@/store/trainingStore';
import { useAgentStore } from '@/store/agentStore';
import { cn } from '@/utils/cn';

interface CreateFormData {
  name: string;
  description: string;
  agent_id: string;
}

const defaultFormData: CreateFormData = {
  name: '',
  description: '',
  agent_id: '',
};

export const TrainingListPage: React.FC = () => {
  const navigate = useNavigate();
  const { grounds, groundsLoading, fetchGrounds, createGround, deleteGround } = useTrainingStore();
  const { agents, fetchAgents } = useAgentStore();

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<CreateFormData>(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchGrounds().catch(() => toast.error('Failed to load training grounds'));
    fetchAgents().catch(() => toast.error('Failed to load agents'));
  }, [fetchGrounds, fetchAgents]);

  const openCreateModal = () => {
    setFormData(defaultFormData);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData(defaultFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.agent_id) {
      toast.error('Please select an agent');
      return;
    }

    setIsSaving(true);
    try {
      await createGround({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        agent_id: formData.agent_id,
      });
      toast.success('Training ground created');
      closeModal();
    } catch {
      toast.error('Failed to create training ground');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, groundId: string, groundName: string) => {
    e.stopPropagation();
    if (!confirm(`Delete "${groundName}"? This action cannot be undone.`)) return;

    try {
      await deleteGround(groundId);
      toast.success('Training ground deleted');
    } catch {
      toast.error('Failed to delete training ground');
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'active'
      ? { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' }
      : { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Training Ground</h1>
          <p className="text-gray-500 mt-1">
            Create training scenarios and practice conversations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/training/reviews')}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 border-2 border-[#1a1a2e] transition-colors"
          >
            <ClipboardCheck className="w-4 h-4" />
            Reviews
          </button>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors shadow-[3px_3px_0px_#1a1a2e]"
          >
            <Plus className="w-4 h-4" />
            Create
          </button>
        </div>
      </div>

      {/* Loading State */}
      {groundsLoading ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
        </div>
      ) : grounds.length === 0 ? (
        /* Empty State */
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-6">
              <GraduationCap className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No training grounds yet
            </h3>
            <p className="text-gray-500 mb-6">
              Create your first training ground to start building practice scenarios for your team.
            </p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors shadow-[3px_3px_0px_#1a1a2e]"
            >
              <Plus className="w-4 h-4" />
              Create Training Ground
            </button>
          </div>
        </div>
      ) : (
        /* Table View */
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1a1a2e] bg-gray-50">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Scenarios</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Sessions</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {grounds.map((ground) => {
                const statusCfg = getStatusBadge(ground.status);
                return (
                  <tr
                    key={ground.id}
                    onClick={() => navigate(`/training/${ground.id}`)}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-500 flex items-center justify-center flex-shrink-0">
                          <GraduationCap className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{ground.name}</p>
                          <p className="text-sm text-gray-500 truncate">{ground.description || 'No description'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{ground.agent_name || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{ground.scenario_count ?? 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{ground.session_count ?? 0}</td>
                    <td className="px-6 py-4">
                      <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold', statusCfg.bg, statusCfg.text)}>
                        <span className={cn('w-1.5 h-1.5 rounded-full', statusCfg.dot)} />
                        {ground.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(ground.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => handleDelete(e, ground.id, ground.name)}
                        className="text-red-500 hover:text-red-600 text-sm inline-flex items-center gap-1.5 font-medium transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-white rounded-xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Create Training Ground</h2>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Customer Service Training"
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the purpose and focus of this training ground..."
                  rows={4}
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm resize-none"
                />
              </div>

              {/* Agent Select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Agent <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.agent_id}
                  onChange={(e) => setFormData({ ...formData, agent_id: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm bg-white"
                >
                  <option value="">Select an agent...</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#1a1a2e]">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || !formData.name.trim() || !formData.agent_id}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingListPage;
